import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const RecommendedItems = ({ cartItems = [], onAddToCart, refreshTrigger }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [recommendedItems, setRecommendedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);

        // 1. Gather all unique category and manufacturer IDs in the cart
        const cartCategoryIds = [...new Set(cartItems.map(item => item.product?.category_id || item.product?.categoryId || item.component?.category_id || null).filter(Boolean))];
        const cartManufacturerIds = [...new Set(cartItems.map(item => item.product?.manufacturer_id || item.product?.manufacturerId || item.component?.manufacturer_id || null).filter(Boolean))];

        // 1.5. Symmetrically fetch all currently in-stock and active seller products/components to prevent recommending out-of-stock items
        const [sellerProdsRes, sellerCompsRes] = await Promise.all([
          supabase
            .from('seller_products')
            .select('product_id')
            .eq('status', 'active')
            .eq('isdeleted', false)
            .gt('stock_amount', 0),
          supabase
            .from('seller_components')
            .select('component_id')
            .eq('status', 'active')
            .eq('isdeleted', false)
            .gt('stock_quantity', 0)
        ]);

        const inStockProductIds = [...new Set((sellerProdsRes.data || []).map(p => p.product_id).filter(Boolean))];
        const inStockComponentIds = [...new Set((sellerCompsRes.data || []).map(c => c.component_id).filter(Boolean))];

        let products = [];
        let components = [];

        // 2. Fetch related Products based on Category or Manufacturer
        let prodQuery = supabase
          .from('products')
          .select('id, name, price, thumbnail_path, category_id, manufacturer_id')
          .eq('status', 'active')
          .in('id', inStockProductIds.length > 0 ? inStockProductIds : ['00000000-0000-0000-0000-000000000000']);
        
        if (cartCategoryIds.length > 0 || cartManufacturerIds.length > 0) {
          const conditions = [];
          if (cartCategoryIds.length > 0) conditions.push(`category_id.in.(${cartCategoryIds.join(',')})`);
          if (cartManufacturerIds.length > 0) conditions.push(`manufacturer_id.in.(${cartManufacturerIds.join(',')})`);
          prodQuery = prodQuery.or(conditions.join(','));
        }
        
        // Filter out items already in the cart
        const existingProdIds = cartItems.map(item => item.productId).filter(Boolean);
        if (existingProdIds.length > 0) {
          prodQuery = prodQuery.not('id', 'in', `(${existingProdIds.join(',')})`);
        }

        const { data: matchedProducts } = await prodQuery.limit(8);
        products = matchedProducts || [];

        // 3. Fetch related Components based on Category or Manufacturer
        let compQuery = supabase
          .from('components')
          .select('id, name, images_url, price')
          .eq('isdeleted', false) // Filter out globally deleted components
          .not('is_active', 'eq', false) // Only retrieve active/enabled components (true or null)
          .in('id', inStockComponentIds.length > 0 ? inStockComponentIds : ['00000000-0000-0000-0000-000000000000'])
          .limit(8);
        const existingCompIds = cartItems.map(item => item.componentId).filter(Boolean);
        if (existingCompIds.length > 0) {
          compQuery = compQuery.not('id', 'in', `(${existingCompIds.join(',')})`);
        }
        const { data: matchedComponents } = await compQuery;
        components = matchedComponents || [];

        // 4. Fallback: If no matches found, pull generic trending items
        if (products.length === 0 && components.length === 0) {
          const { data: fallbackProducts } = await supabase
            .from('products')
            .select('id, name, price, thumbnail_path')
            .eq('status', 'active')
            .in('id', inStockProductIds.length > 0 ? inStockProductIds : ['00000000-0000-0000-0000-000000000000'])
            .limit(5);
          products = fallbackProducts || [];
        }

        // 5. Transform and combine
        const transformedProducts = products.map(p => ({
          id: p.id,
          name: p.name,
          price: parseFloat(p.price) || 0,
          image: p.thumbnail_path,
          imageAlt: `${p.name} thumbnail`,
          isComponent: false,
          productId: p.id
        }));

        const transformedComponents = components.map(c => {
          const baseName = c.name;
          let displayName = baseName;
          if (c.product?.name) {
            displayName = `${baseName} for ${c.product.name}`;
          }
          return {
            id: c.id,
            name: displayName,
            price: parseFloat(c.price) || 0,
            image: c.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'Component')}&background=6366f1&color=fff&size=128`,
            imageAlt: `${c.name} thumbnail`,
            isComponent: true,
            productId: null
          };
        });

        // Mix products and components, shuffle and limit to 10
        const combined = [...transformedProducts, ...transformedComponents]
          .sort(() => 0.5 - Math.random())
          .slice(0, 10);

        setRecommendedItems(combined);
      } catch (err) {
        console.error('Error fetching recommendations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [cartItems, refreshTrigger]);

  const handleScroll = () => {
    if (!scrollContainerRef?.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef?.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction) => {
    if (!scrollContainerRef?.current) return;
    const scrollAmount = 300;
    const newScrollLeft = direction === 'left' 
      ? scrollContainerRef?.current?.scrollLeft - scrollAmount
      : scrollContainerRef?.current?.scrollLeft + scrollAmount;
    
    scrollContainerRef?.current?.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft mt-8">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (recommendedItems.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft mt-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Sparkles" size={20} className="text-primary fill-primary/10 animate-pulse" />
          <h3 className="text-lg font-semibold text-foreground">Recommended For You</h3>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => scroll('left')}
            disabled={!showLeftArrow}
            className="disabled:opacity-30"
          >
            <Icon name="ChevronLeft" size={18} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => scroll('right')}
            disabled={!showRightArrow}
            className="disabled:opacity-30"
          >
            <Icon name="ChevronRight" size={18} />
          </Button>
        </div>
      </div>

      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {recommendedItems.map((item) => (
          <div
            key={item.id}
            className="flex-shrink-0 flex flex-col items-center space-y-2 group"
          >
            <Link to={item.isComponent ? `/3d-model-viewer?id=${item.productId}&componentId=${item.id}` : `/3d-model-viewer?id=${item.id}`}>
              <div className="w-24 h-24 bg-muted rounded-lg overflow-hidden hover:scale-110 transition-transform shadow-sm">
                <Image
                  src={item.image}
                  alt={item.imageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            </Link>

            <div className="text-center">
              <Link to={item.isComponent ? `/3d-model-viewer?id=${item.productId}&componentId=${item.id}` : `/3d-model-viewer?id=${item.id}`}>
                <p className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-1 max-w-[120px]">
                  {item.name}
                </p>
              </Link>
              <p className="text-base font-bold text-foreground mt-1">
                ${item.price.toFixed(2)}
              </p>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddToCart(item.id, item.isComponent)}
                className="mt-2 h-8 px-3"
              >
                <Icon name="ShoppingCart" size={14} />
                <span className="ml-1 text-xs">Add</span>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecommendedItems;
